import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { type Base, convertAll } from "../lib/base";

const SAMPLE = "255";

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function OutputRow({
  label,
  value,
  testid,
}: {
  label: string;
  value: string;
  testid: string;
}) {
  const id = useId();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input
          id={id}
          readOnly
          data-testid={testid}
          value={value}
          spellCheck={false}
          className="font-mono"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={copy}
          aria-label={`${m.nb_copy()} ${label}`}
          className="shrink-0"
        >
          {copied ? (
            <Check className="h-4 w-4 text-emerald-500" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}

export function NumberBaseTool() {
  const uid = useId();
  const [input, setInput] = useState(SAMPLE);
  const [fromBase, setFromBase] = useState<Base>(10);

  const result = useMemo(() => convertAll(input, fromBase), [input, fromBase]);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 rounded-2xl border border-border bg-card/60 p-6 sm:grid-cols-2 sm:p-8">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-in`}>{m.nb_input_label()}</Label>
          <Input
            id={`${uid}-in`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={m.nb_placeholder()}
            spellCheck={false}
            autoComplete="off"
            className="font-mono text-lg"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-base`}>{m.nb_from_label()}</Label>
          <select
            id={`${uid}-base`}
            className={selectClass}
            value={fromBase}
            onChange={(e) => setFromBase(Number(e.target.value) as Base)}
          >
            <option value={2}>{m.nb_base2()}</option>
            <option value={8}>{m.nb_base8()}</option>
            <option value={10}>{m.nb_base10()}</option>
            <option value={16}>{m.nb_base16()}</option>
          </select>
        </div>
      </div>

      {result.ok ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <OutputRow label={m.nb_bin()} value={result.bin} testid="nb-bin" />
          <OutputRow label={m.nb_oct()} value={result.oct} testid="nb-oct" />
          <OutputRow label={m.nb_dec()} value={result.dec} testid="nb-dec" />
          <OutputRow label={m.nb_hex()} value={result.hex} testid="nb-hex" />
        </div>
      ) : (
        <p
          data-testid="nb-error"
          className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-300"
        >
          {m.nb_invalid()}
        </p>
      )}
    </div>
  );
}
